<template>
  <div>
    <BaseModal
      v-model="isOpen"
      title="Formas de acesso"
      modal-width="560px"
      modal-height="auto"
      max-height-body="auto"
      hide-primary-button
      secondary-button-text="Fechar"
      :secondary-button-action="close"
    >
      <p class="identities__intro">
        Formas que você pode usar para entrar no sistema.
      </p>

      <v-progress-linear
        v-if="loading"
        indeterminate
        color="primary"
      />

      <ul
        v-else
        class="identities__list"
      >
        <li class="identities__item">
          <PhEnvelopeSimple
            :size="24"
            class="identities__icon"
          />
          <div class="identities__body">
            <span class="identities__name">E-mail e senha</span>
            <span class="identities__detail">{{ userEmail }}</span>
          </div>
          <Chip
            variant="tonal"
            tonal-color="success"
            small
          >
            Sempre ativo
          </Chip>
        </li>

        <li
          v-for="identity in identities"
          :key="identity.id"
          class="identities__item"
          data-test="item-forma-acesso"
        >
          <ProviderLogo
            :provider="identity.provider"
            :size="24"
            class="identities__icon"
          />
          <div class="identities__body">
            <span class="identities__name">{{ identity.label }}</span>
            <span class="identities__detail">{{ identity.email }}</span>
            <span class="identities__meta">{{ usageText(identity) }}</span>
          </div>
          <Button
            variant="tertiary"
            data-test="btn-desvincular"
            @click="askUnlink(identity)"
          >
            <PhLinkBreak
              :size="18"
              class="mr-1"
            />
            Desvincular
          </Button>
        </li>
      </ul>

      <EmptyState
        v-if="!loading && !identities.length"
        title="Nenhuma conta vinculada"
        subtitle="Para vincular, saia do sistema e clique em Fazer login com o Google na tela de entrada."
        min-height="160px"
        data-test="estado-sem-vinculos"
      />
    </BaseModal>

    <ConfirmationWindow
      v-model="confirmOpen"
      :title="confirmTitle"
      :message="confirmMessage"
      text-primary-button="Desvincular"
      text-secondary-button="Cancelar"
      :callback-primary="unlink"
      :callback-secondary="() => (confirmOpen = false)"
    />
  </div>
</template>

<script>
import UserIdentityApi from '@/api/user/user-identity-api';
import mixinMessage from '@/mixin/mixin-message';
import BaseModal from '@/components/comum/BaseModal.vue';
import Chip from '@/components/comum/Chip.vue';
import EmptyState from '@/components/comum/EmptyState.vue';
import Button from '@/components/comum/Button.vue';
import ConfirmationWindow from '@/components/comum/ConfirmationWindow.vue';
import { authErrorMessage } from '@/utils/auth-error-message';
import ProviderLogo from './ProviderLogo.vue';
const TIME_ZONE = 'America/Sao_Paulo';

function formatDate(value) {
  return new Date(value).toLocaleDateString('pt-BR', { timeZone: TIME_ZONE });
}

function formatTime(value) {
  return new Date(value).toLocaleTimeString('pt-BR', { timeZone: TIME_ZONE, hour: '2-digit', minute: '2-digit' });
}

/**
 * "Formas de acesso" do usuário logado (menu do usuário): e-mail e senha (sempre ativo) e as
 * contas de provedor vinculadas, com desvincular. Todo texto vem por interpolação (nunca v-html).
 */
export default {
  name: 'UserIdentitiesModal',
  components: { BaseModal, Button, Chip, EmptyState, ConfirmationWindow, ProviderLogo },
  mixins: [mixinMessage],
  inject: ['appData'],
  model: { prop: 'open', event: 'onChange' },
  props: {
    open: { type: Boolean, default: false }
  },
  data() {
    return {
      api: new UserIdentityApi(this.appData.currentUser),
      identities: [],
      loading: false,
      confirmOpen: false,
      selected: null
    };
  },
  computed: {
    isOpen: {
      get() {
        return this.open;
      },
      set(value) {
        this.$emit('onChange', value);
      }
    },
    userEmail() {
      return this.appData.currentUser && this.appData.currentUser.email;
    },
    confirmTitle() {
      return this.selected ? `Desvincular conta ${this.selected.label}?` : '';
    },
    confirmMessage() {
      if (!this.selected) return '';

      return `Você não vai mais conseguir entrar com ${this.selected.email || `essa conta ${this.selected.label}`}. `
        + 'Seu e-mail e senha continuam funcionando. Se quiser, pode vincular de novo depois.';
    }
  },
  watch: {
    open: {
      immediate: true,
      handler(value) {
        if (value) this.load();
      }
    }
  },
  methods: {
    async load() {
      this.loading = true;

      try {
        const { data } = await this.api.list();

        this.identities = Array.isArray(data) ? data : [];
      } catch (error) {
        this.identities = [];
        this.sendErrorMessage(error, 'Não foi possível carregar suas formas de acesso. Tente de novo.');
      } finally {
        this.loading = false;
      }
    },

    usageText(identity) {
      const linked = `Vinculada em ${formatDate(identity.createdAt)}`;

      if (!identity.lastLoginAt) return `${linked} · Ainda não usada para entrar`;

      return `${linked} · Último acesso em ${formatDate(identity.lastLoginAt)} às ${formatTime(identity.lastLoginAt)}`;
    },

    askUnlink(identity) {
      this.selected = identity;
      this.confirmOpen = true;
    },

    async unlink() {
      const identity = this.selected;

      this.confirmOpen = false;

      try {
        await this.api.unlink(identity.id);
        this.identities = this.identities.filter((item) => item.id !== identity.id);
        this.sendMessage(`Conta ${identity.label} desvinculada.`, 'success');
      } catch (error) {
        this.sendMessage(authErrorMessage(error, identity.label), 'error');

        if (error && error.response && error.response.status === 404) this.load();
      } finally {
        this.selected = null;
      }
    },

    close() {
      this.isOpen = false;
    }
  }
};
</script>

<style lang="scss" scoped>
.identities__intro {
  margin: 0 0 var(--tc-space-4);
  color: var(--tc-text-2);
  font-size: var(--tc-fs-sm);
}

.identities__list {
  display: flex;
  flex-direction: column;
  gap: var(--tc-space-2);
  margin: 0;
  padding: 0;
  list-style: none;
}

.identities__item {
  display: flex;
  align-items: center;
  gap: var(--tc-space-3);
  padding: var(--tc-space-3) var(--tc-space-4);
  border: 1px solid var(--tc-line);
  border-radius: var(--tc-radius-md);
  background: var(--tc-surface);
}

.identities__icon {
  flex-shrink: 0;
  color: var(--tc-text-2);
}

.identities__body {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}

.identities__name {
  color: var(--tc-text);
  font-weight: var(--tc-fw-semibold);
}

.identities__detail,
.identities__meta {
  overflow-wrap: anywhere;
  color: var(--tc-text-2);
  font-size: var(--tc-fs-sm);
}

.identities__meta {
  color: var(--tc-text-3);
}
</style>
